import {
  BookOpenText,
  Check,
  ChevronDown,
  Copy,
  ExternalLink,
  Files,
  FileText,
  Landmark,
  MapPin,
  Scale,
  Globe2,
} from "lucide-react";
import { useState } from "react";
import {
  apaReferenceParts,
  normalizeCitationReference,
} from "@tribunalea/shared";
import { CopyableDate } from "@tribunalea/ui";
import {
  citationTypeLabel,
  normalizeCitation,
  safeSourceUrl,
} from "./sources.js";

const typeIcons = {
  legislation: Landmark,
  doctrine: BookOpenText,
  jurisprudence: Scale,
  case_record: Files,
  attachment: FileText,
  library: Files,
  official_web: Globe2,
};

const dateFormatter = new Intl.DateTimeFormat("es-AR", {
  dateStyle: "medium",
});

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : dateFormatter.format(date);
}

function isTribunaleaUrl(value) {
  if (!value) return false;
  try {
    const hostname = new URL(value).hostname;
    return hostname === "tribunalea.com"
      || hostname.endsWith(".tribunalea.com")
      || hostname === "localhost"
      || hostname === "127.0.0.1";
  } catch {
    return false;
  }
}

export function CitationCard({
  citation: rawCitation,
  index,
  linkMode = "both",
}) {
  const citation = normalizeCitation(rawCitation);
  const formattedReference = normalizeCitationReference(citation);
  const apaParts = apaReferenceParts(citation);
  const [copied, setCopied] = useState("");
  const Icon = typeIcons[citation.type] || BookOpenText;
  const sourceUrl = safeSourceUrl(citation.source?.url);
  const resolvedDirectUrl = safeSourceUrl(
    citation.tribunaleaUrl
      || citation.source?.tribunaleaUrl
      || citation.metadata?.tribunaleaUrl,
  ) || (isTribunaleaUrl(sourceUrl) ? sourceUrl : null);
  const resolvedOriginalUrl = sourceUrl && sourceUrl !== resolvedDirectUrl
    ? sourceUrl
    : null;
  const directUrl = linkMode === "original" ? null : resolvedDirectUrl;
  const originalUrl = linkMode === "tribunalea"
    ? null
    : resolvedOriginalUrl || (
      linkMode === "original" && sourceUrl ? sourceUrl : null
    );
  const validFrom = formatDate(citation.metadata?.validFrom);
  const validTo = formatDate(citation.metadata?.validTo);
  const organization =
    citation.source?.organization || citation.source?.title || null;

  async function copyCitation(value, kind) {
    if (!value || typeof navigator === "undefined") return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
      window.setTimeout(() => setCopied(""), 1_800);
    } catch {
      setCopied("");
    }
  }

  return (
    <details className={`justiniano-citation is-${citation.type}`}>
      <summary>
        <span className="justiniano-citation-number">{index + 1}</span>
        <span className="justiniano-citation-heading">
          <span className="justiniano-citation-kind">
            <Icon size={12} />
            {citationTypeLabel(citation.type)}
            {(citation.source?.official || citation.metadata?.official) && (
              <em>Oficial</em>
            )}
          </span>
          {citation.type === "doctrine" ? (
            <span className="justiniano-citation-heading-reference">
              {apaParts ? (
                <>
                  {apaParts.authors} ({apaParts.year}).{" "}
                  <cite>{apaParts.title}</cite>
                  {apaParts.edition ? ` (${apaParts.edition})` : ""}.{" "}
                  {apaParts.publisher}.
                  {apaParts.url ? <> {apaParts.url}</> : null}
                </>
              ) : formattedReference.bibliography}
            </span>
          ) : <strong>{citation.label}</strong>}
        </span>
        <ChevronDown className="justiniano-citation-chevron" size={15} />
      </summary>
      <div className="justiniano-citation-body">
        {citation.metadata?.fixture && (
          <div className="justiniano-citation-fixture">
            Fuente ficticia de demostración
          </div>
        )}
        {citation.quoteText && <blockquote>{citation.quoteText}</blockquote>}
        <div className="justiniano-citation-meta">
          {organization && <span>{organization}</span>}
          {citation.locator && citation.locator !== citation.label && (
            <span>{citation.locator}</span>
          )}
          {validFrom && (
            <span>
              Vigente desde{" "}
              <CopyableDate value={citation.metadata.validFrom}>
                {validFrom}
              </CopyableDate>
            </span>
          )}
          {validTo && (
            <span>
              Vigente hasta{" "}
              <CopyableDate value={citation.metadata.validTo}>
                {validTo}
              </CopyableDate>
            </span>
          )}
        </div>
        {(citation.type === "doctrine" || directUrl || originalUrl) && (
          <div className="justiniano-citation-actions">
            {citation.type === "doctrine" && (
              <>
                <button
                  onClick={() => copyCitation(
                    formattedReference.shortCitation,
                    "citation",
                  )}
                  type="button"
                >
                  {copied === "citation" ? <Check size={12} /> : <Copy size={12} />}
                  {copied === "citation" ? "Cita copiada" : "Copiar cita"}
                </button>
                <button
                  onClick={() => copyCitation(
                    formattedReference.bibliography,
                    "reference",
                  )}
                  type="button"
                >
                  {copied === "reference" ? <Check size={12} /> : <Copy size={12} />}
                  {copied === "reference" ? "Referencia copiada" : "Copiar referencia"}
                </button>
              </>
            )}
            {directUrl && (
              <a href={directUrl} target="_blank" rel="noreferrer">
                <MapPin size={12} /> Abrir fuente en Tribunalea
              </a>
            )}
            {originalUrl && (
              <a href={originalUrl} target="_blank" rel="noreferrer">
                Fuente original externa <ExternalLink size={12} />
              </a>
            )}
          </div>
        )}
      </div>
    </details>
  );
}
